// src/components/Navigation.jsx
// UPDATED: Uses actual SuddyFlow logo

import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useBusinessContext } from '../contexts/BusinessContext';
import { X, Menu } from 'lucide-react';

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useBusinessContext();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const isLoggedIn = !!user;
  
  const isAppRoute = ['/dashboard', '/customers', '/jobs', '/leads', '/invoices', '/expenses', '/settings'].some(
    route => location.pathname.startsWith(route)
  );
  
  const isAuthPage = ['/login', '/register', '/trial-expired'].includes(location.pathname);
  
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);
  
  if (isAppRoute || isAuthPage) {
    return null;
  }

  const handleLogout = async () => {
    await logout();
    navigate('/login');
    setMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <>
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link to={isLoggedIn ? "/dashboard" : "/"} className="flex items-center gap-3">
                <img 
                  src="/images/SuddyFlow-Logo-PNG.png" 
                  alt="SuddyFlow" 
                  className="h-8 object-contain"
                />
                <span className="ml-1 text-xs bg-blue-900 text-white px-2 py-1 rounded font-semibold flex items-center gap-1">
                  🇺🇸 VET
                </span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {!isLoggedIn ? (
                <>
                  <Link 
                    to="/features" 
                    className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
                  >
                    Features
                  </Link>
                  <Link 
                    to="/pricing" 
                    className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
                  >
                    Pricing
                  </Link>
                  <Link 
                    to="/about" 
                    className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
                  >
                    About
                  </Link>
                  <Link 
                    to="/contact" 
                    className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
                  >
                    Contact
                  </Link>
                  <div className="h-6 w-px bg-gray-300"></div>
                  <Link 
                    to="/login" 
                    className="text-blue-600 hover:text-blue-700 font-semibold transition-colors"
                  >
                    Sign In
                  </Link>
                  <Link 
                    to="/register" 
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-sm"
                  >
                    Start Free Trial
                  </Link>
                </>
              ) : (
                <>
                  <Link 
                    to="/dashboard" 
                    className="text-blue-600 hover:text-blue-700 font-semibold transition-colors"
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="text-gray-700 hover:text-red-600 font-medium transition-colors"
                  >
                    Sign Out
                  </button>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button 
                onClick={toggleMobileMenu}
                className="text-gray-700 hover:text-blue-600 p-2"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu Slide-out Panel */}
      <div 
        className={`fixed top-0 right-0 h-full w-64 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out md:hidden ${
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <img 
                src="/images/Icon-PNG.png" 
                alt="SuddyFlow" 
                className="h-8 w-8 object-contain"
              />
              <span className="text-xl font-bold text-blue-800">Menu</span>
            </div>
            <button 
              onClick={() => setMobileMenuOpen(false)}
              className="text-gray-500 hover:text-gray-700 p-2"
              aria-label="Close menu"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Mobile Menu Items */}
          <div className="flex-1 overflow-y-auto py-4">
            {!isLoggedIn ? (
              <div className="space-y-1 px-4">
                <Link 
                  to="/features" 
                  className="block py-3 px-4 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors font-medium"
                >
                  Features
                </Link>
                <Link 
                  to="/pricing" 
                  className="block py-3 px-4 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors font-medium"
                >
                  Pricing
                </Link>
                <Link 
                  to="/about" 
                  className="block py-3 px-4 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors font-medium"
                >
                  About
                </Link>
                <Link 
                  to="/contact" 
                  className="block py-3 px-4 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors font-medium"
                >
                  Contact
                </Link>
                
                <div className="h-px bg-gray-200 my-4"></div>
                
                <Link 
                  to="/login" 
                  className="block py-3 px-4 text-blue-600 hover:bg-blue-50 rounded-lg font-semibold transition-colors"
                >
                  Sign In
                </Link>
                <Link 
                  to="/register" 
                  className="block py-3 px-4 bg-blue-600 text-white text-center rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-sm"
                >
                  Start Free Trial
                </Link>
              </div>
            ) : (
              <div className="space-y-1 px-4">
                <Link 
                  to="/dashboard" 
                  className="block py-3 px-4 text-blue-600 hover:bg-blue-50 rounded-lg font-semibold transition-colors"
                >
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="w-full text-left py-3 px-4 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-lg font-medium transition-colors"
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>

          {/* Mobile Menu Footer */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center justify-center space-x-2">
              <span className="text-xs text-gray-500">🇺🇸 Veteran-Owned</span>
              <span className="text-gray-300">•</span>
              <span className="text-sm font-semibold text-blue-600">SuddyFlow</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;